import { EntityResolved } from '@api/api/entities';
import { Entity } from '@api/api/entity-schema';
import { RowSelectionState } from '@tanstack/react-table';
import { FilterGroups } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useAssetsFilterEntityTags } from '@terminal/react-query-hooks/useAssetsFilterEntityTags';
import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  getRemovedFilterTagsNotProtected,
  mergeHydratedEntitiesIntoList,
  tagsEqual,
  toEntityResolved,
} from '../utils';
import { useScrollPaginatedEntities } from './useScrollPaginatedEntities';

type EntitiesListUpdate =
  | EntityResolved[]
  | ((prev: EntityResolved[]) => EntityResolved[]);

export type UseWatchlistFilterSyncParams = {
  filterGroups: FilterGroups<'ASSET_FILTERS'>;
  containerRef: RefObject<HTMLDivElement | null>;
  entitiesListLength: number;
  setEntitiesList: (update: EntitiesListUpdate) => void;
  setRowSelection: (
    update: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)
  ) => void;
  enabled?: boolean;
  prefillTags?: string[];
  manualEntityTags?: string[];
};

export const useWatchlistFilterSync = ({
  filterGroups,
  containerRef,
  entitiesListLength,
  setEntitiesList,
  setRowSelection,
  enabled = true,
  prefillTags = [],
  manualEntityTags = [],
}: UseWatchlistFilterSyncParams) => {
  const stableFilterTagsRef = useRef<string[]>([]);
  const lastSyncedKeyRef = useRef('');

  const { data: resolvedFilterTags = [], isLoading: isFilterTagsLoading } =
    useAssetsFilterEntityTags(filterGroups);

  const stableFilterTags = !enabled
    ? []
    : isFilterTagsLoading
      ? stableFilterTagsRef.current
      : resolvedFilterTags;

  const protectedTags = useMemo(
    () => new Set([...manualEntityTags, ...prefillTags]),
    [manualEntityTags, prefillTags]
  );

  const hydrationTags = useMemo(
    () => [...new Set([...prefillTags, ...stableFilterTags])],
    [prefillTags, stableFilterTags]
  );

  const isHydrationEnabled =
    enabled && !isFilterTagsLoading && hydrationTags.length > 0;

  const {
    entities: hydratedEntities,
    loadedPageCount,
    hasNextPage,
    isLoading: isHydrationQueryLoading,
    isFetchingNextPage,
  } = useScrollPaginatedEntities(hydrationTags, isHydrationEnabled, {
    scrollContainerRef: containerRef,
    scrollFillKey: entitiesListLength,
  });

  const isHydratingEntities = isHydrationEnabled && isHydrationQueryLoading;

  useEffect(
    function applyStableFilterTagChanges() {
      if (!enabled || isFilterTagsLoading) {
        return;
      }

      const previousTags = stableFilterTagsRef.current;
      if (tagsEqual(previousTags, resolvedFilterTags)) {
        return;
      }

      const removedTags = previousTags.filter(
        tag => !resolvedFilterTags.includes(tag)
      );
      const addedTags = resolvedFilterTags.filter(
        tag => !previousTags.includes(tag)
      );

      if (!!removedTags.length || !!addedTags.length) {
        lastSyncedKeyRef.current = '';
      }

      const filterRemovedTags = getRemovedFilterTagsNotProtected(
        removedTags,
        protectedTags
      );

      if (!!filterRemovedTags.length) {
        const filterRemoved = new Set(filterRemovedTags);

        setRowSelection(prev =>
          Object.fromEntries(
            Object.entries(prev).filter(([tag]) => !filterRemoved.has(tag))
          )
        );
        setEntitiesList(prev =>
          prev.filter(entity => !filterRemoved.has(entity.tag))
        );
      }

      if (addedTags.length > 0) {
        setRowSelection(prev => ({
          ...prev,
          ...Object.fromEntries(addedTags.map(tag => [tag, true])),
        }));
      }

      stableFilterTagsRef.current = resolvedFilterTags;
    },
    [
      enabled,
      isFilterTagsLoading,
      resolvedFilterTags,
      protectedTags,
      setEntitiesList,
      setRowSelection,
    ]
  );

  useLayoutEffect(
    function syncHydratedEntities() {
      if (!isHydrationEnabled || loadedPageCount === 0) {
        return;
      }

      const syncKey = `${hydrationTags.join('\0')}:${loadedPageCount}`;
      if (syncKey === lastSyncedKeyRef.current) {
        return;
      }
      lastSyncedKeyRef.current = syncKey;

      setEntitiesList(prev =>
        mergeHydratedEntitiesIntoList(prev, {
          hydrated: (hydratedEntities as Entity[]).map(entity =>
            toEntityResolved(entity)
          ),
          position: 'prepend',
        })
      );
    },
    [
      isHydrationEnabled,
      loadedPageCount,
      hydrationTags,
      hydratedEntities,
      setEntitiesList,
    ]
  );

  return {
    isEntityTagsLoading: enabled && isFilterTagsLoading,
    isHydratingEntities,
    hasNextPage: isHydrationEnabled && hasNextPage,
    isLoadingEntities:
      isHydrationEnabled && (isHydrationQueryLoading || isFetchingNextPage),
    filterTags: stableFilterTags,
  };
};
